import { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Sparkles, Trash2, AlertTriangle, ListChecks, Loader2, Activity, RefreshCw, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AiReviewSettingsCard({ workspaceId, role }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [listOpen, setListOpen] = useState(false);
  // D88+ — workspace AI review health (counts + recent failures)
  const [health, setHealth] = useState(null);
  const [retryAllBusy, setRetryAllBusy] = useState(false);
  const [retryAllConfirm, setRetryAllConfirm] = useState(false);

  const canEdit = ['owner', 'admin', 'super_admin'].includes((role || '').toLowerCase());

  const load = async () => {
    if (!workspaceId) return;
    try {
      const r = await fetch(`${API}/api/workspaces/${workspaceId}/ai-review`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('stellar_token')}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
    } catch (e) {
      // silently ignore — feature still works without UI
    }
  };

  const loadHealth = async () => {
    if (!workspaceId) return;
    try {
      const r = await fetch(`${API}/api/workspaces/${workspaceId}/ai-review/health`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('stellar_token')}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setHealth(await r.json());
    } catch (_) {}
  };

  useEffect(() => { load(); loadHealth(); }, [workspaceId]);

  // D88+ — refresh health every 30s while the card is mounted (cheap aggregation).
  useEffect(() => {
    if (!workspaceId) return;
    const t = setInterval(loadHealth, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  const retryAllFailed = async () => {
    if (!canEdit) return;
    setRetryAllBusy(true);
    try {
      const r = await fetch(`${API}/api/workspaces/${workspaceId}/ai-review/retry-all-failed`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('stellar_token')}` },
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${r.status}`);
      }
      const body = await r.json();
      toast.success(`Re-queued ${body.queued} review(s) across ${body.submissions_kicked} submission(s)`);
      setRetryAllConfirm(false);
      loadHealth();
    } catch (e) {
      toast.error(`Retry all failed: ${e.message || 'unknown'}`);
    } finally {
      setRetryAllBusy(false);
    }
  };

  const toggle = async (val) => {
    if (!canEdit) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/workspaces/${workspaceId}/ai-review`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('stellar_token')}`,
        },
        body: JSON.stringify({ enabled: !!val }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${r.status}`);
      }
      toast.success(val ? 'AI content review enabled' : 'AI content review disabled');
      load();
    } catch (e) {
      toast.error(`Failed: ${e.message || 'unknown'}`);
    } finally {
      setBusy(false);
    }
  };

  const removeEntry = async (idx) => {
    if (!canEdit) return;
    try {
      const r = await fetch(`${API}/api/workspaces/${workspaceId}/ai-review/dismiss/${idx}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('stellar_token')}` },
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${r.status}`);
      }
      toast.success('Entry removed');
      load();
    } catch (e) {
      toast.error(`Failed: ${e.message || 'unknown'}`);
    }
  };

  if (!data) return null;

  const list = data.dismiss_list || [];

  return (
    <Card className="bg-card/60 backdrop-blur" data-testid="ai-review-settings-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-gold" /> AI Content Review
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            <p className="text-[12px] font-medium">Enable AI content review on submissions</p>
            <p className="text-[11px] text-muted-foreground/70 mt-0.5 leading-relaxed">
              When enabled, uploaded images and PDFs are automatically scanned for spelling and grammar issues.
              AI suggestions are not always correct — brand names, intentional capitalization, and hashtags may
              be flagged. Dismissed flags reduce noise over time.
            </p>
          </div>
          <Switch
            checked={!!data.enabled}
            onCheckedChange={toggle}
            disabled={!canEdit || busy}
            data-testid="ai-review-toggle"
          />
        </div>

        {!data.api_key_configured && (
          <div className="rounded-md border border-amber-700/30 bg-amber-900/10 px-3 py-2 text-[11px] text-amber-300 flex items-center gap-2">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
            <span>No API key configured (OPENAI_API_KEY or EMERGENT_LLM_KEY). Reviews will be skipped silently.</span>
          </div>
        )}
        {data.api_key_configured && (
          <div className="text-[10px] text-muted-foreground/60">
            Provider: <span className="text-foreground/80 font-medium">{data.model}</span>
            {' · '}Key source: <span className="text-foreground/80 font-medium">
              {data.api_key_source === 'openai_direct' ? 'OPENAI_API_KEY (direct)' : 'EMERGENT_LLM_KEY (universal)'}
            </span>
          </div>
        )}

        <div className="border-t border-border/20 pt-3 flex items-start justify-between">
          <div>
            <p className="text-[12px] font-medium flex items-center gap-1.5">
              <ListChecks className="h-3.5 w-3.5 text-muted-foreground/70" />
              Workspace dismiss list
            </p>
            <p className="text-[11px] text-muted-foreground/70 mt-0.5">
              {data.dismiss_list_count} {data.dismiss_list_count === 1 ? 'entry' : 'entries'} suppressed from future reviews
            </p>
          </div>
          {canEdit && (
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-[11px]"
              onClick={() => setListOpen(true)}
              data-testid="ai-review-view-dismiss-list"
            >
              View / edit
            </Button>
          )}
        </div>

        <div className="border-t border-border/20 pt-3">
          <p className="text-[11px] text-muted-foreground/70 mb-1">
            AI reviews this month ({data.usage?.month})
          </p>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-display text-gold" data-testid="ai-review-month-calls">
              {data.usage?.month_calls ?? 0}
            </span>
            <span className="text-[11px] text-muted-foreground/60">
              calls · estimated cost{' '}
              <span className="text-foreground/80 font-medium" data-testid="ai-review-month-cost">
                ${(data.usage?.month_estimated_cost_usd ?? 0).toFixed(3)}
              </span>
            </span>
          </div>
          <p className="text-[10px] text-muted-foreground/50 mt-1">
            Lifetime: {data.usage?.lifetime_calls ?? 0} calls · ${(data.usage?.lifetime_estimated_cost_usd ?? 0).toFixed(3)}
            {' '}({data.usage?.cost_per_call_usd ?? 0.001}/call)
          </p>
        </div>

        {/* D88+ — Review health (live counts + Retry all failed) */}
        {health && (
          <div className="border-t border-border/20 pt-3" data-testid="ai-review-health-section">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1">
                <p className="text-[12px] font-medium flex items-center gap-1.5">
                  <Activity className="h-3.5 w-3.5 text-muted-foreground/70" />
                  Review health
                </p>
                <p className="text-[11px] text-muted-foreground/70 mt-0.5">
                  Live snapshot of every uploaded file's review state. Auto-refreshes every 30s.
                </p>
              </div>
              {canEdit && (health.counts?.failed || 0) > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-[11px]"
                  onClick={() => setRetryAllConfirm(true)}
                  disabled={retryAllBusy}
                  data-testid="ai-review-retry-all"
                >
                  {retryAllBusy ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RefreshCw className="h-3 w-3 mr-1" />}
                  Retry all failed ({health.counts.failed})
                </Button>
              )}
            </div>
            <div className="mt-3 grid grid-cols-4 gap-2">
              <div className="rounded-md border border-border/30 bg-foreground/[0.02] p-2" data-testid="ai-review-count-pending">
                <p className="text-[10px] text-muted-foreground/70">Pending</p>
                <p className={`text-lg font-display ${(health.counts?.pending || 0) > 0 ? 'text-amber-400' : 'text-foreground/40'}`}>
                  {health.counts?.pending || 0}
                </p>
              </div>
              <div className="rounded-md border border-border/30 bg-foreground/[0.02] p-2" data-testid="ai-review-count-failed">
                <p className="text-[10px] text-muted-foreground/70">Failed</p>
                <p className={`text-lg font-display ${(health.counts?.failed || 0) > 0 ? 'text-red-400' : 'text-foreground/40'}`}>
                  {health.counts?.failed || 0}
                </p>
              </div>
              <div className="rounded-md border border-border/30 bg-foreground/[0.02] p-2" data-testid="ai-review-count-completed">
                <p className="text-[10px] text-muted-foreground/70">Completed</p>
                <p className="text-lg font-display text-emerald-400">{health.counts?.completed || 0}</p>
              </div>
              <div className="rounded-md border border-border/30 bg-foreground/[0.02] p-2" data-testid="ai-review-count-skipped">
                <p className="text-[10px] text-muted-foreground/70">Skipped</p>
                <p className="text-lg font-display text-foreground/40">{health.counts?.skipped || 0}</p>
              </div>
            </div>
            {(health.recent_failures || []).length > 0 && (
              <div className="mt-3" data-testid="ai-review-recent-failures">
                <p className="text-[10px] text-muted-foreground/70 mb-1.5">Recent failures</p>
                <div className="space-y-1">
                  {health.recent_failures.map((f, i) => (
                    <div
                      key={`${f.task_id}-${f.file_id}`}
                      className="flex items-center gap-2 px-2 py-1.5 rounded-md border border-red-700/20 bg-red-900/[0.06] text-[11px]"
                      data-testid={`ai-review-recent-failure-${i}`}
                    >
                      <XCircle className="h-3 w-3 text-red-400 shrink-0" />
                      <span className="flex-1 truncate">
                        <span className="font-medium">{f.task_title}</span>
                        <span className="text-muted-foreground/70"> · {f.filename} (V{f.version})</span>
                      </span>
                      <span className="text-[10px] text-red-300/70 truncate max-w-[180px]" title={f.error_message}>
                        {f.error_message || f.reason || 'error'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>

      <Dialog open={listOpen} onOpenChange={setListOpen}>
        <DialogContent className="sm:max-w-[520px] bg-card border-border/40">
          <DialogHeader>
            <DialogTitle className="text-[14px] font-semibold">Workspace dismiss list</DialogTitle>
            <p className="text-[11px] text-muted-foreground/60 mt-1">
              These strings will not be flagged in future AI reviews. Remove an entry if it was wrongly dismissed.
            </p>
          </DialogHeader>
          <div className="max-h-[400px] overflow-y-auto space-y-1.5" data-testid="ai-dismiss-list-modal">
            {list.length === 0 && (
              <div className="text-[12px] text-muted-foreground/60 text-center py-6">
                No entries yet. As reviewers dismiss flags as "brand name" or "false positive",
                they'll appear here.
              </div>
            )}
            {list.map((e, i) => (
              <div
                key={i}
                className="flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-foreground/[0.03] border border-border/20 text-[12px]"
                data-testid={`ai-dismiss-entry-${i}`}
              >
                <span className="flex-1 font-medium truncate" title={e.original}>{e.original}</span>
                <span className="text-[10px] text-muted-foreground/60 shrink-0">{e.reason}</span>
                {canEdit && (
                  <button
                    onClick={() => removeEntry(i)}
                    className="text-muted-foreground/50 hover:text-destructive shrink-0"
                    data-testid={`ai-dismiss-remove-${i}`}
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setListOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* D88+ — Retry all failed confirmation */}
      <Dialog open={retryAllConfirm} onOpenChange={setRetryAllConfirm}>
        <DialogContent className="sm:max-w-md bg-card border-border/40">
          <DialogHeader>
            <DialogTitle className="text-[14px] font-semibold">Retry all failed AI reviews?</DialogTitle>
            <p className="text-[11px] text-muted-foreground/70 mt-1">
              This will re-queue every <span className="text-red-300 font-medium">{health?.counts?.failed || 0}</span> failed
              review in this workspace and run them through the AI pipeline again. Large videos may take a few minutes.
            </p>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRetryAllConfirm(false)} disabled={retryAllBusy}>Cancel</Button>
            <Button onClick={retryAllFailed} disabled={retryAllBusy} data-testid="ai-review-retry-all-confirm">
              {retryAllBusy ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RefreshCw className="h-3 w-3 mr-1" />}
              Retry all
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
